<template>
  <div>
    <div class="invalidQuiz" v-if="invalid">
      <h2 class="invalidQuiz__Title">Invalid Quiz</h2>
      <p class="invalidQuiz__Description">
        Looks like this link leads to no quiz or the quiz has been disabled. Contact the person you got this link from
        to know more.
      </p>
    </div>
    <template v-else>
      <loader class="imageLoader" :loading="true" v-if="loading" />
      <template v-else>
        <header class="quiz__Header" v-if="hasHeader">
          <a href="https://publicservices.international">
            <img src="@/assets/images/logo-psi.svg" alt="" />
            <h1>Public Services International</h1>
          </a>
        </header>
        <article class="quiz" :style="{ backgroundColor, backgroundImage }">
          <div class="translations" v-if="quiz && quiz.l10n">
            <router-link
              class="translations__Item"
              :class="{ 'router-link-exact-active': lang === translation.lang }"
              v-for="translation in quiz.l10n"
              :to="queryLanguage(translation.lang)"
              :key="translation.lang"
              >{{ translation.lang }}</router-link
            >
          </div>
          <button class="share" @click="shareModal = true"><icon weight="solid" name="share" /> Share</button>
          <button
            class="showChart"
            @click="showChart = !showChart"
            v-if="activeCharts.length > 0 || activeScores.length > 0"
          >
            <icon weight="solid" name="chart-bar" />
          </button>
          <transition name="fade">
            <section class="shareModal" v-if="shareModal">
              <button class="shareModal__Wrapper" @click="shareModal = false">Close</button>
              <div class="shareModal__Content">
                <button class="shareModal__Close" @click="shareModal = false"><icon name="times" /></button>
                <h3 class="shareModal__Title">Share this quiz</h3>
                <p class="shareModal__Text">
                  Share this quiz with someone or embed it directly with your website using the provided HTML code.
                </p>
                <p>
                  <label class="formLabel" for="shareLink">Quiz URL</label>
                  <input
                    id="shareLink"
                    class="formInput formInput--Code"
                    type="text"
                    :value="shareLink"
                    readonly
                    @click="copyToClipboard(shareLink)"
                  />
                </p>
                <p>
                  <label class="formLabel" for="shareEmbed">Embed code</label>
                  <textarea
                    id="shareEmbed"
                    required
                    class="formInput--Textarea formInput--Code"
                    v-model="shareEmbed"
                    readonly
                    @click="copyToClipboard(shareEmbed)"
                  ></textarea>
                </p>
              </div>
            </section>
          </transition>
          <nav class="quiz__SliderPagination" :class="{ 'has-Header': hasHeader }">
            <ul class="quiz__SliderPaginationList">
              <li
                class="quiz__SliderPaginationItem"
                v-for="(slide, index) in slides"
                :class="[
                  { 'is-Active': currentSlide && currentSlide.id === slide.id },
                  { 'is-Disabled': firstEmptySlide && index > firstEmptySlide },
                  { 'quiz__SliderPaginationItem--Question': slide.answers },
                ]"
                :key="slide.id"
                @click="firstEmptySlide && index > firstEmptySlide ? null : goToSlide(index)"
              >
                <span class="quiz__SliderPaginationButton">{{ slide.name }}</span>
              </li>
            </ul>
          </nav>
          <form class="quiz__Form">
            <hooper
              ref="quizSlider"
              class="quiz__Slider"
              :vertical="true"
              :itemsToShow="1"
              :wheelControl="false"
              :touchDrag="false"
              :mouseDrag="false"
              @slide="handleSlideChange"
            >
              <slide v-for="(slide, index) in slides" :key="`quiz-section-${index}`">
                <div class="quiz__Section">
                  <h2 class="quiz__SectionTitle">{{ slide.name }}</h2>
                  <div class="quiz__SectionContent">
                    <div v-html="slide.text" class="quiz__SectionContentInner" />
                    <div class="quiz__Buttons">
                      <button
                        type="button"
                        class="quiz__Continue"
                        @click="nextSlide"
                        v-if="!slide.answers && slides.length - 1 !== index"
                      >
                        Continue
                      </button>
                      <button type="button" class="quiz__Continue--PDF" @click="generateReport" v-if="slide.show_pdf">
                        Download PDF
                      </button>
                      <button
                        type="button"
                        class="quiz__Continue"
                        @click="goToSlide(firstSlideWithQuestions)"
                        v-if="index === slides.length - 1"
                      >
                        Go to start
                      </button>
                    </div>
                  </div>
                  <component
                    v-if="slide.type"
                    :is="slide.type"
                    :data="slide"
                    :outcomes="slide.outcomes"
                    :answers="answers"
                    :clickable="clickable"
                    @input="handleSelectAnswer"
                  />
                </div>
              </slide>
              <hooper-progress slot="hooper-addons"></hooper-progress>
            </hooper>
          </form>
          <transition name="fade">
            <aside
              class="quiz__Graph"
              key="quiz-graph-1"
              v-if="chartNav.length > 0"
              :class="{ 'is-Active': showChart }"
            >
              <nav class="quiz__GraphNav" v-if="chartNav.length > 1">
                <ul class="quiz__GraphList">
                  <li class="quiz__GraphNavItem" v-for="chart in chartNav" :key="chart.id">
                    <button
                      class="quiz__GraphNavButton"
                      :class="{ 'is-Active': activeChart.id === chart.id }"
                      @click="selectedChart = chart"
                    >
                      {{ chart.name }}
                    </button>
                  </li>
                </ul>
              </nav>
              <transition name="fade">
                <div class="quiz__GraphContent" v-if="activeChart.datasets">
                  <spider-graph :chart-data="activeChart" :options="options" />
                </div>
                <ul class="quiz__Scores" v-else>
                  <li class="quiz__Score" v-for="score in activeScores" :key="score.id">
                    <h3 class="quiz__ScoreTitle">{{ score.name }}</h3>
                    <p class="quiz__ScoreValue">{{ score.score.toFixed(1) }}</p>
                  </li>
                </ul>
              </transition>
            </aside>
          </transition>
        </article>
        <vue-html2pdf
          ref="html2Pdf"
          :show-layout="false"
          :float-layout="true"
          :manual-pagination="true"
          :html-to-pdf-options="{
            filename: `quiz-report-${+new Date()}`,
            margin: [20, 0, 20, 0],
            pagebreak: { mode: ['avoid-all'] },
            html2canvas: {
              dpi: 300,
              letterRendering: true,
              useCORS: true,
            },
            jsPDF: {
              format: 'a4',
            },
          }"
        >
          <div slot="pdf-content">
            <div class="pdfContent">
              <div class="pdfContent__Inner">
                <h2 class="pdfContent__MainTitle">{{ data.title }}</h2>
                <div v-html="slides[0].text" />
                <h2 class="pdfContent__Title" v-if="selectedAnswers.length > 0">Your answers</h2>
                <ul class="pdfContent__Answers">
                  <li v-for="slide in selectedAnswers" :key="slide.id">
                    <h4>{{ slide.title }}</h4>
                    <ul>
                      <li v-for="answer in slide.answers" :key="answer.id">
                        {{ answer.label }}
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
              <section class="pdfContent__Charts">
                <h2 class="pdfContent__Title">Your results</h2>
                <div class="pdfContent__ChartList">
                  <div class="pdfContent__Chart" v-for="chart in charts.filter((c) => c.datasets)" :key="chart.id">
                    <h3 class="pdfContent__Subtitle">{{ chart.name }}</h3>
                    <spider-graph :chart-data="chart" :options="options" />
                  </div>
                </div>
              </section>
              <div class="pdfContent__Inner">
                <h2 class="pdfContent__Title">Your overall score</h2>
                <ul class="pdfContent__Scores">
                  <li class="pdfContent__Score" v-for="score in charts.filter((c) => !c.datasets)" :key="score.id">
                    <h3 class="pdfContent__ScoreTitle">{{ score.name }}</h3>
                    <p class="pdfContent__ScoreValue">{{ score.score.toFixed(1) }}</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </vue-html2pdf>
      </template>
    </template>
  </div>
</template>

<script>
import 'hooper/dist/hooper.css'

import HTTP from '@/http-common.js'
import GetTranslationObject from '@/components/mixins/GetTranslationObject.js'
import CopyToClipboard from '@/components/mixins/CopyToClipboard.js'
import Operators from '@/components/mixins/Operators.js'
import VueHtml2pdf from 'vue-html2pdf'
import Loader from '@/components/partials/Loader.vue'
import { isLocalStorageEnabled } from '@/utils'

import { Hooper, Slide, Progress as HooperProgress, Navigation as HooperNavigation } from 'hooper'
import SpiderGraph from './SpiderGraph.js'
import Buttons from './blocks/Buttons.vue'

export default {
  components: {
    Hooper,
    Slide,
    HooperProgress,
    HooperNavigation,
    SpiderGraph,
    Buttons,
    VueHtml2pdf,
    Loader,
  },
  mixins: [GetTranslationObject, CopyToClipboard, Operators],
  computed: {
    lang() {
      return this.$route.query.lang || this.$store.state.ui.lang
    },
    data() {
      return this.quiz.l10n && this.getTranslationObject(this.quiz.l10n, this.lang)
    },
    getChartByID() {
      return (id) => this.charts.find((chart) => chart.id === id)
    },
    hasHeader() {
      return this.$route.query.website === 'asa095f2-1525-477f-aw80-640a3e41a8de'
    },
    slides() {
      return (
        this.data &&
        JSON.parse(JSON.stringify(this.data))
          .sections.reduce((acc, section) => {
            if (section.questions) {
              const questions = section.questions.map((q) => {
                return {
                  ...q,
                  background_color: section.background_color,
                }
              })
              delete section.questions
              return [...acc, section, ...questions]
            }

            return [...acc, section]
          }, [])
          .filter((section) => {
            if (section.conditionals && section.conditionals.length > 0) {
              return section.conditionals.every(
                (c) =>
                  this.compareValues(
                    this.getChartByID(c.first).score,
                    this.getChartByID(c.second).score,
                    c.operator
                  ) === true
              )
            }
            return true
          })
      )
    },
    firstEmptySlide() {
      const index = this.slides.findIndex((s) => s.answers && !this.answers.map((a) => a.question_id).includes(s.id))
      return index === -1 ? null : index
    },
    firstSlideWithQuestions() {
      return this.slides.findIndex((s) => s.answers)
    },
    charts() {
      return this.data.outcomes.map((chart) => {
        // Get answers for specific outcome/chart ID
        const answers = this.answers.filter((a) => a.outcomes.map((o) => o.id).includes(chart.id))

        if (chart.type === 'summary') {
          return {
            name: chart.name,
            id: chart.id,
            score: answers.reduce((acc, answer) => {
              return acc + answer.score / answers.length
            }, 0),
          }
        } else {
          return {
            name: chart.name,
            id: chart.id,
            score: answers.reduce((acc, answer) => {
              return acc + answer.score / answers.length
            }, 0),
            labels: chart.fields.map((f) => f.label),
            datasets: [
              {
                label: chart.name,
                data: chart.fields.map((f) => {
                  const fieldAnswers = answers.filter((a) => a.outcomes.map((o) => o.field_id).includes(f.id)) // Get answers for specific field within given outcome/chart
                  return fieldAnswers.reduce((acc, answer) => {
                    return acc + answer.score / fieldAnswers.length
                  }, 0)
                }),
                backgroundColor: 'rgba(0, 26, 255, 0.1)',
                borderColor: 'rgb(0, 26, 255)',
              },
            ],
          }
        }
      })
    },
    activeCharts() {
      if (this.currentSlide) {
        const ids = this.currentSlide.outcomes.map((o) => o.id)
        return this.charts.filter((chart) => chart.datasets && ids.includes(chart.id))
      }
      return []
    },
    activeScores() {
      if (this.currentSlide) {
        const ids = this.currentSlide.outcomes.map((o) => o.id)
        return this.charts.filter((chart) => !chart.datasets && ids.includes(chart.id))
      }
      return []
    },
    activeChart() {
      return this.selectedChart || (this.chartNav && this.chartNav[0])
    },
    chartNav() {
      return [
        ...this.activeCharts,
        ...(this.activeScores[0] ? [{ id: this.activeScores[0].id, name: 'Scores', scores: this.activeScores }] : []),
      ]
    },
    backgroundColor() {
      return this.currentSlide && this.currentSlide.background_color
    },
    backgroundImage() {
      return `url(${this.quiz.contents.thumbnail_urls && this.quiz.contents.thumbnail_urls.original})`
    },
    selectedAnswers() {
      return this.slides
        .reduce((acc, slide) => {
          if (slide.answers) {
            return [
              ...acc,
              {
                title: slide.name,
                answers: slide.answers.filter((f) => this.answers.map((a) => a.answer_id).includes(f.id)),
              },
            ]
          }
          return acc
        }, [])
        .filter((slide) => slide.answers.length > 0)
    },

    shareLink() {
      return `${window.location.origin}/quiz/${this.quiz.id}`
    },
    shareEmbed() {
      return (
        this.data &&
        `<iframe width="560" height="315" src="${this.shareLink}" title="${this.data.title}" frameborder="0" allowfullscreen></iframe>`
      )
    },
  },
  created() {
    const id = this.$route.params.id
    const api_keys = [this.$route.query.website]
    const lang = this.$route.query.lang
    document.title = 'Loading...'

    const setInvalid = () => {
      this.loading = false
      this.invalid = true
      document.title = 'Invalid Quiz'
    }

    if (id !== undefined && api_keys.length > 0) {
      HTTP.post(`resource/${id}`, {
        api_keys,
      })
        .then((response) => {
          this.quiz = response.data
          this.loading = false
          document.title = this.getTranslationObject(response.data.l10n, lang).title
        })
        .then(() => {
          if (isLocalStorageEnabled()) {
            const store = JSON.parse(localStorage.getItem(`motyro_quiz_${id}_answers`))
            if (store && store.length > 0) {
              this.answers = store
              if (this.firstEmptySlide === null) {
                this.goToSlide(this.slides.length - 1)
              } else {
                this.goToSlide(this.firstEmptySlide)
              }
            } else {
              this.answers = []
            }
          }
        })
        .catch((e) => {
          this.errors = e.response ? e.response.data : console.error(e)
          setInvalid()
        })
    } else {
      setInvalid()
    }
  },
  data() {
    return {
      loading: true,
      quiz: {},
      answers: [],
      invalid: false,
      currentSlide: null,
      currentSlideIndex: 0,
      shareModal: false,
      selectedChart: null,
      clickable: true,
      showChart: false,
      options: {
        legend: false,
        layout: {
          padding: window.innerWidth > 768 ? 30 : 10,
        },
        scale: {
          ticks: {
            suggestedMin: 0,
            suggestedMax: 5,
          },
          pointLabels: {
            fontSize: window.innerWidth > 768 ? 14 : 9,
          },
        },
      },
    }
  },
  methods: {
    handleSlideChange(slide) {
      this.currentSlide = this.slides[slide.currentSlide]
      this.currentSlideIndex = slide.currentSlide
      this.selectedChart = null

      if (this.answers.length > 0)
        localStorage.setItem(`motyro_quiz_${this.quiz.id}_answers`, JSON.stringify(this.answers))
    },
    nextSlide() {
      this.$refs.quizSlider.slideNext()
    },
    goToSlide(num) {
      this.$refs.quizSlider.slideTo(num)
    },
    handleSelectAnswer(payload) {
      const answer = this.answers.find((a) => a.question_id === payload.question_id)
      if (answer) {
        answer.answer_id = payload.answer_id
        answer.score = payload.score
      } else {
        this.answers.push(payload)
      }

      this.clickable = false
      setTimeout(() => {
        this.nextSlide()
        this.clickable = true
      }, 500)
    },
    generateReport() {
      this.$refs.html2Pdf.generatePdf()
    },
    queryLanguage(lang) {
      return {
        query: {
          ...this.$route.query,
          lang,
        },
      }
    },
  },
}
</script>

<style lang="scss">
:root {
  --motyro-title-font: 'Egyptienne', Georgia, serif;
  --motyro-label-font: 'MaisonMono', Arial, sans-serif;
  --motyro-quiz-brand: #001aff;
  --motyro-quiz-bg: #e6d8c8;
}

.quiz__Header {
  padding: 10px 15px;
  align-items: center;
  position: fixed;
  background: white;
  z-index: 100;
  left: 0;
  right: 0;

  a {
    color: inherit;
    text-decoration: none;
    display: flex;
  }

  img {
    max-width: 40px;
    display: block;
  }

  h1 {
    font-family: var(--motyro-label-font);
    max-width: 200px;
    font-size: 16px;
    text-transform: uppercase;
    margin-left: 20px;
  }
}

.quiz__Slider {
  .hooper-progress {
    width: 100vw;
  }

  .hooper-progress-inner {
    background: var(--motyro-quiz-brand);
  }
}
</style>

<style lang="scss" scoped>
.quiz {
  background: #e6d8c8;
  background-size: 500px;
  transition: 300ms;
}

.quiz__Slider {
  min-height: 100vh;
  width: 100%;

  .hooper-slide {
    display: flex;
    align-items: center;
  }

  &.has-Header {
    padding-top: 60px;
  }
}

.quiz__Section {
  display: block;
  padding: 40px 10%;
  overflow-y: auto;
  max-height: 100vh;

  @media only screen and (max-width: 768px) {
    padding: 15px 15px 50px;
  }
}

.quiz__SectionTitle {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 20px;
  font-family: var(--motyro-title-font);

  @media only screen and (max-width: 768px) {
    font-size: 24px;
    margin-top: 100px;
  }
}

.quiz__SectionContent {
  line-height: 1.66;
  font-size: 18px;

  @media only screen and (max-width: 768px) {
    font-size: 14px;
  }
}

.quiz__SectionContentInner {
  ::v-deep li {
    p {
      margin: 0;
    }

    a {
      color: black;
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    }
  }
}

.quiz__SliderPagination {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  z-index: 20;

  @media only screen and (max-width: 768px) {
    display: none;
  }
}

.quiz__SliderPaginationList {
  margin: 0;
  padding: 0;
  display: block;
}

%quiz__SliderPaginationItem,
.quiz__SliderPaginationItem {
  display: block;
  padding: 7px 15px;
  position: relative;
  transition: 300ms;
  cursor: pointer;

  &::after {
    content: '';
    display: block;
    width: 15px;
    height: 15px;
    background: black;
    transition: 300ms;
    transform: scale(0.5) rotate(45deg);
    border-radius: 50%;
  }

  &:hover {
    .quiz__SliderPaginationButton {
      margin-left: 20px;
      opacity: 1;
      visibility: visible;
    }
    &::after {
      transform: scale(1) rotate(45deg);
    }
  }

  &.is-Active {
    &::after {
      transform: scale(1) rotate(45deg);
    }
  }

  &.is-Disabled {
    pointer-events: none;
    opacity: 0.5;
  }
}

.quiz__SliderPaginationItem--Question {
  &::after {
    border-radius: 0;
  }
}

.quiz__SliderPaginationButton {
  all: unset;
  position: absolute;
  white-space: nowrap;
  margin-left: 20px;
  line-height: 30px;
  box-sizing: border-box;
  margin-top: -15px;
  top: 50%;
  color: white;
  background: black;
  border-radius: 4px;
  padding: 0 10px;
  font-size: 12px;
  text-transform: uppercase;
  font-family: var(--motyro-label-font);
  transition: 300ms;
  opacity: 0;
  visibility: hidden;
}

.quiz__Buttons {
  display: flex;
}

%quiz__Continue,
.quiz__Continue {
  all: unset;
  background: black;
  color: white;
  padding: 0 30px;
  line-height: 45px;
  height: 44px;
  border-radius: 5px;
  margin-top: 20px;
  font-weight: bold;
  font-size: 15px;
  cursor: pointer;
  transition: 200ms;
  margin-right: 20px;
  box-sizing: border-box;

  &:hover {
    background: lighten(black, 30%);
  }
}

.quiz__Continue--PDF {
  @extend %quiz__Continue;
  border: 2px solid black;
  background: white;
  color: black;

  &:hover {
    color: white;
  }
}

.quiz__Form {
  width: 50%;
  transition: 500ms;
  z-index: 10;
  position: relative;

  @media only screen and (max-width: 768px) {
    width: 100%;
  }
}

.quiz__Graph {
  flex: 1;
  position: fixed;
  left: 50%;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: transparent;
  transition: 500ms;
  background-color: white;
  display: flex;
  align-items: center;

  @media only screen and (max-width: 768px) {
    z-index: 20;
    left: 0;
    opacity: 0;
    visibility: hidden;

    &.is-Active {
      opacity: 1;
      visibility: visible;
    }
  }
}

.quiz__GraphContent {
  position: absolute;
  left: 5%;
  right: 5%;
  top: 80px;
}

.quiz__GraphNav {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
}

.quiz__GraphList {
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
}

.quiz__GraphNavItem {
  display: block;
  flex: 1;
}

.quiz__GraphNavButton {
  all: unset;
  padding: 30px 30px 20px;
  border-bottom: 3px solid #efefef;
  cursor: pointer;
  width: 100%;
  text-align: center;
  box-sizing: border-box;

  &:hover {
    border-bottom-color: #dedede;
  }

  &.is-Active {
    border-bottom-color: var(--motyro-quiz-brand);
  }

  @media only screen and (max-width: 768px) {
    padding: 20px 10px;
    font-size: 12px;
  }
}

.quiz__Scores {
  display: flex;
  flex-flow: row wrap;
  margin: 0;
  padding: 0;
  margin: 0 auto;
  align-items: flex-end;

  @media only screen and (max-width: 768px) {
    display: block;
  }
}

.quiz__Score {
  display: block;
  text-align: center;
  padding: 0 50px;

  @media only screen and (max-width: 768px) {
    padding: 20px;
  }
}

.quiz__ScoreTitle {
  font-family: var(--motyro-label-font);
  font-size: 14px;
  font-weight: normal;
  text-transform: uppercase;
  max-width: 200px;
  margin: 0 auto;
  color: #858585;
}

.quiz__ScoreValue {
  font-size: 5vmax;
  margin: 20px 0;

  @media only screen and (max-width: 768px) {
    font-size: 10vmax;
  }
}

.showChart {
  all: unset;
  background: black;
  box-sizing: border-box;
  padding: 5px;
  border-radius: 50%;
  color: white;
  position: fixed;
  z-index: 30;
  bottom: 15px;
  right: 20px;
  display: none;
  padding: 10px;

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }

  @media only screen and (max-width: 768px) {
    display: block;
  }
}

.share {
  all: unset;
  background: black;
  color: white;
  padding: 10px 20px;
  position: absolute;
  right: 20px;
  bottom: 20px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  border-radius: 5px;
  font-size: 12px;
  font-family: var(--motyro-label-font);
  text-transform: uppercase;
  cursor: pointer;
  z-index: 20;

  svg {
    width: 15px;
    height: 15px;
    margin-right: 10px;
  }

  @media only screen and (max-width: 768px) {
    display: none;
  }
}

.translations {
  position: fixed;
  right: 10px;
  top: 10px;
  display: flex;
  z-index: 200;
}

.translations__Item {
  text-transform: uppercase;
  color: black;
  font-size: 12px;
  display: block;
  text-decoration: none;
  padding: 10px;
  font-weight: bold;
  opacity: 0.4;

  &:hover {
    opacity: 1;
    color: var(--motyro-quiz-brand);
  }

  &.router-link-exact-active {
    color: black;
    opacity: 1;
  }
}

.shareModal {
  position: fixed;
  z-index: 200;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(black, 0.7);
  display: flex;
}

.shareModal__Wrapper {
  all: unset;
  font-size: 0;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.shareModal__Content {
  width: 500px;
  background-color: white;
  padding: 20px;
  z-index: 10;
  margin: auto;
  position: relative;
  position: relative;
  z-index: 10;
}

.shareModal__Close {
  all: unset;
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }
}

.sharaeModal__Title {
  font-family: var(--motyro-title-font);
  margin: 0;
  font-size: 24px;
  font-weight: bold;
}

.shareModal__Text {
  font-size: 16px;
  color: #858585;
}

.pdfContent {
  padding: 50px;
}

.pdfContent__MainTitle {
  font-size: 4em;
  line-height: 1;
  font-weight: normal;
  max-width: 50%;
}

.pdfContent__Inner {
  font-size: 12pt;
  line-height: 1.66;
}

.pdfContent__Chart {
  // page-break-after: always;
}

.pdfContent {
  font-family: var(--motyro-title-font);
  overflow: none;
  height: auto;
}

.pdfContent__Title {
  font-size: 2em;
  font-weight: bold;
  margin-bottom: 20px;
  page-break-before: always;
}

.pdfContent__Subtitle {
  font-size: 1.5em;
  font-weight: bold;
  margin-bottom: 20px;
}

.pdfContent__Scores {
  display: flex;
  justify-content: space-between;
  padding: 30px 0;
  max-width: 80%;
  margin: 0 auto;
}

.pdfContent__Score {
  display: flex;
  flex-direction: column;
}

.pdfContent__ScoreTitle {
  order: 2;
  text-align: center;
  margin-bottom: 20px;
}

.pdfContent__ScoreValue {
  margin: auto 0;
  font-size: 3em;
  border: 4px solid var(--motyro-quiz-brand);
  width: 150px;
  height: 150px;
  line-height: 150px;
  margin: 0 auto;
  border-radius: 50%;
  text-align: center;
}

.invalidQuiz {
  display: flex;
  height: 100vh;
  padding: 20px;
  text-align: center;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.invalidQuiz__Title {
  font-weight: normal;
  font-family: var(--motyro-title-font);
  font-size: 36px;
  margin: 0 0 10px;
}

.invalidQuiz__Description {
  font-size: 18px;
  max-width: 600px;
  line-height: 1.66;
  margin: 0;
}
</style>
